import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useClientPreset } from '@/contexts/ClientPresetContext'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const isAdmin = user?.role?.toLowerCase() === 'admin'
  const { selectedPreset } = useClientPreset()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const navigation = [
    { name: 'Assets', href: '/upload', current: location.pathname === '/upload' },
    { name: 'Asset Editor', href: '/asset-editor', current: location.pathname === '/asset-editor' },
    { name: 'Clients', href: '/presets', current: location.pathname === '/presets' },
    { name: 'Campaigns', href: '/advanced-campaign', current: location.pathname === '/advanced-campaign' },
    { name: 'Analytics', href: '/analytics', current: location.pathname === '/analytics' },
    ...(isAdmin ? [
      { name: 'User Management', href: '/users', current: location.pathname === '/users' },
      { name: 'Investors', href: '/investors', current: location.pathname === '/investors' },
    ] : []),
  ]

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-systemGroupedBackground)' }}>
      {/* Navigation */}
      <nav className="main-navigation">
        {/* Desktop Navigation */}
        <div className="nav-desktop">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-8)' }}>
            <h1 className="header-brand">
              Martimart Ads Platform
            </h1>
            <div className="nav-links-container">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link ${item.current ? 'active' : ''}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="header-right">
            {/* Selected Preset Indicator */}
            {selectedPreset && (
              <div className="selected-client-badge">
                <div
                  className="client-color-indicator"
                  style={{
                    backgroundColor: selectedPreset.primaryColor,
                  }}
                />
                <span className="client-name">
                  {selectedPreset.brand}
                </span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="logout-button"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="nav-mobile">
          <div className="nav-mobile-header">
            <h1 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-label)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}>
              Martimart Ads Platform
            </h1>
            <button
              className="hamburger-button"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <div className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>

          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
              <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                {/* Selected Preset */}
                {selectedPreset && (
                  <div className="mobile-preset-indicator">
                    <div
                      style={{
                        width: 'var(--spacing-1\.5)',
                        height: 'var(--spacing-1\.5)',
                        borderRadius: 'var(--border-radius-full)',
                        backgroundColor: selectedPreset.primaryColor,
                      }}
                    />
                    <span>{selectedPreset.brand}</span>
                  </div>
                )}

                {/* Navigation Links */}
                <div className="mobile-nav-links">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`mobile-nav-link ${item.current ? 'active' : ''}`}
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* User Info & Logout */}
                <div className="mobile-user-section">
                  <span className="mobile-user-email">{user?.email}</span>
                                      <button
                    onClick={() => {
                      handleLogout()
                      closeMobileMenu()
                    }}
                    className="mobile-logout-btn"
                    style={{
                      padding: 'var(--spacing-2\.5) var(--spacing-3)',
                      borderRadius: 'var(--radius-button)',
                      background: 'var(--color-primary)',
                      color: 'var(--color-systemBackground)',
                      border: 'none',
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'var(--transition-backgroundColor)',
                      boxShadow: 'var(--shadow-button)',
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  )
} 